import { EmojiEvents, Search, Star } from '@mui/icons-material';
import { alpha, Autocomplete, Box, Button, Chip, Paper, Stack, TextField, Typography, useTheme } from '@mui/material';
import { MAX_SUPER_POWERS } from '@sharedInterfaces/globalEnums';
import type { ISkillReferenceDTO } from '@sharedInterfaces/ISkill';
import markSkillAsSuperPower from '@src/APIs/graphQl/Employee/markSkillAsSuperPower';
import reorderSuperPowers from '@src/APIs/graphQl/Employee/reorderSuperPowers';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, type DropResult } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';

export function SuperPowersSection() {
    const theme = useTheme();
    const showErrorDialog = useErrorDialog();
    const employee = useSelector((state: AppState) => state.employee);
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);
    const offline = useSelector((state: AppState) => state.client.offline);

    const [superPowers, setSuperPowers] = useState<ISkillReferenceDTO[]>([]);
    const [eligibleSkills, setEligibleSkills] = useState<ISkillReferenceDTO[]>([]);

    const maxLevel = levelDefinitions.length > 0 ? Math.max(...levelDefinitions.map((l) => l.level)) : 4;
    const minSuperPowerLevel = Math.max(1, maxLevel - 1);

    useEffect(() => {
        const sp = employee.skills.filter((s) => s.isSuperPower === true);
        const order = employee.superPowerOrder || [];

        // Apply order
        if (order.length > 0) {
            const orderMap = new Map(order.map((id, index) => [id, index]));
            sp.sort((a, b) => {
                const orderA = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
                const orderB = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
            });
        }

        // Double-check: Only include skills that are actually SuperPowers
        const validSuperPowers = sp.filter((s) => s.isSuperPower === true);
        setSuperPowers(validSuperPowers);

        // Eligible skills (not yet SuperPowers)
        const eligible = employee.skills.filter(
            (s) => s.level >= minSuperPowerLevel && s.isSuperPower !== true,
        );
        setEligibleSkills(eligible);
    }, [employee.skills, employee.superPowerOrder, minSuperPowerLevel]);

    const handleAddSuperPower = async (skillId: number) => {
        if (superPowers.length >= MAX_SUPER_POWERS) {
            showErrorDialog(new Error(`Maximal ${MAX_SUPER_POWERS} SuperPowers erlaubt`), 'Limit erreicht');
            return;
        }

        try {
            await markSkillAsSuperPower(skillId, true);
        } catch (error) {
            showErrorDialog(error as Error, 'Fehler beim Hinzufügen der SuperPower');
        }
    };

    const handleRemoveSuperPower = async (skillId: number) => {
        try {
            await markSkillAsSuperPower(skillId, false);
        } catch (error) {
            showErrorDialog(error as Error, 'Fehler beim Entfernen der SuperPower');
        }
    };

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(superPowers);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setSuperPowers(items);

        try {
            await reorderSuperPowers(items.map((item) => item.id));
        } catch (error) {
            showErrorDialog(error as Error, 'Fehler beim Sortieren');
            // Rollback to original order
            const sp = employee.skills.filter((s) => s.isSuperPower === true);
            const order = employee.superPowerOrder || [];
            if (order.length > 0) {
                const orderMap = new Map(order.map((id, index) => [id, index]));
                sp.sort((a, b) => {
                    const orderA = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
                    const orderB = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
                    return orderA - orderB;
                });
            }
            setSuperPowers(sp);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                overflow: 'hidden',
                mb: 3,
            }}
        >
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                    p: 3,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <EmojiEvents sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Deine SuperPowers
                    </Typography>
                    <Chip
                        label={`${superPowers.length}/${MAX_SUPER_POWERS}`}
                        size="small"
                        color={superPowers.length >= MAX_SUPER_POWERS ? 'success' : 'default'}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    Wähle bis zu {MAX_SUPER_POWERS} deiner stärksten Skills (Level {minSuperPowerLevel}+) und sortiere
                    sie per Drag & Drop
                </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
                {/* Current SuperPowers with Drag & Drop */}
                {superPowers.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                            ✨ Deine SuperPowers ({superPowers.length}/{MAX_SUPER_POWERS})
                        </Typography>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="superpowers">
                                {(provided) => (
                                    <Stack spacing={1} {...provided.droppableProps} ref={provided.innerRef}>
                                        {superPowers.map((skill, index) => (
                                            <Draggable
                                                key={skill.id}
                                                draggableId={skill.id.toString()}
                                                index={index}
                                                isDragDisabled={offline}
                                            >
                                                {(provided, snapshot) => (
                                                    <Paper
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        elevation={snapshot.isDragging ? 4 : 0}
                                                        sx={{
                                                            p: 2,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                                            borderRadius: 2,
                                                            cursor: offline ? 'default' : 'grab',
                                                            bgcolor: snapshot.isDragging
                                                                ? alpha(theme.palette.primary.main, 0.1)
                                                                : 'background.paper',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                minWidth: 32,
                                                                height: 32,
                                                                borderRadius: '50%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                                                color: 'primary.main',
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </Box>
                                                        <Star sx={{ color: 'primary.main' }} />
                                                        <Typography sx={{ flex: 1, fontWeight: 500 }}>
                                                            {skill.title}
                                                        </Typography>
                                                        <Chip label={`Level ${skill.level}`} size="small" />
                                                        <Button
                                                            size="small"
                                                            onClick={() => handleRemoveSuperPower(skill.id)}
                                                            disabled={offline}
                                                            sx={{ minWidth: 'auto' }}
                                                        >
                                                            Entfernen
                                                        </Button>
                                                    </Paper>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </Stack>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </Box>
                )}

                {/* Eligible Skills - Autocomplete */}
                {eligibleSkills.length > 0 && superPowers.length < MAX_SUPER_POWERS && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                            🏆 Berechtigte Skills (Level {minSuperPowerLevel}+)
                        </Typography>
                        <Autocomplete
                            options={eligibleSkills}
                            getOptionLabel={(option) => `${option.title} (Level ${option.level})`}
                            onChange={(_, value) => {
                                if (value) {
                                    handleAddSuperPower(value.id);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Skill suchen und als SuperPower markieren..."
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <Search sx={{ mr: 1, color: 'text.secondary' }} />
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            disabled={offline}
                            sx={{ maxWidth: 500 }}
                        />
                    </Box>
                )}

                {/* Empty State */}
                {superPowers.length === 0 && eligibleSkills.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <EmojiEvents sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                            Noch keine SuperPowers
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Füge Skills mit Level {minSuperPowerLevel}+ hinzu, um sie als SuperPowers zu markieren
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
}
